import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Tag, Progress, Button, message, Space, Popconfirm, Tooltip, Card, Flex, Grid, Typography } from 'antd';
import { DeleteOutlined, DownloadOutlined, ExportOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { getDataExports, deleteDataExport } from './DataExportImportServices';
import { Resource, Action } from '../../Constants/socket';
import useSocketEvent from '../../Hooks/useSocketEvent';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import useSocket from '../../Hooks/useSocket';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import DataExportForm from './DataExportForm';
import { RESOURCE_LABEL_MAP } from './constants';
import AppTable from '../Shared/AppTable/AppTable';
import PageToolbar from '../Shared/PageToolbar';
import SharedFilterBar from '../Shared/Filters/SharedFilterBar';
import { dataExportFilterFields } from '../Shared/Filters/filterConfig';
import useFilterOptionFields, { FILTER_OPTION_RESOURCE } from '../Shared/Filters/useFilterOptionFields';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';

const { useBreakpoint } = Grid;
const { Text } = Typography;
const DATA_EXPORT_FILTER_OPTION_RESOURCES = {
    user_ids: { resource: FILTER_OPTION_RESOURCE.USER, mode: 'multiple' },
};
const ADMIN_ONLY_FILTER_KEYS = new Set(['all_users', 'user_ids']);

const DataExportList = () => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const { user } = useGlobal();
    const { handleError } = useErrorHandler();
    const isAdmin = !!user?.is_admin;
    const visibleFilterFields = useMemo(
        () => isAdmin
            ? dataExportFilterFields
            : dataExportFilterFields.filter(field => !ADMIN_ONLY_FILTER_KEYS.has(field.key)),
        [isAdmin]
    );
    const filterFields = useFilterOptionFields(visibleFilterFields, DATA_EXPORT_FILTER_OPTION_RESOURCES);
    const { socket } = useSocket();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState([]);
    const [normalizedFilters, setNormalizedFilters] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const fetchAbortControllerRef = useRef(null);

    // Subscribe to DATA_EXPORT resource
    const exportIds = useMemo(() => data.map(item => item.id), [data]);
    const exportCursors = useMemo(() => data.filter(item => item.id).map(item => ({ id: item.id, fromTimestamp: item.updated_at })), [data]);
    useSocketSubscription(Resource.DATA_EXPORT, exportIds, false, null, exportCursors);

    // Listen for updates
    const handleExportUpdate = useCallback((updatedExport) => {
        setData(prevData => prevData.map(item => {
            if (item.id === updatedExport.id) {
                return {
                    ...item,
                    ...updatedExport,
                    status: {
                        ...(item.status || {}),
                        message_code: updatedExport.status_message_code || item.status?.message_code,
                        message_data: updatedExport.status_message_data || item.status?.message_data,
                    },
                    progress: updatedExport.progress !== undefined ? updatedExport.progress : item.progress
                };
            }
            return item;
        }));
    }, []);

    const handleExportCreate = useCallback((data) => {
        fetchData(pagination.current, normalizedFilters);

        // Subscribe to new export from its creation time to cover the readiness gap
        if (data?.id && data?.created_at && socket) {
            socket.subscribeToResource(Resource.DATA_EXPORT, [data.id], data.created_at);
        }
    }, [pagination.current, normalizedFilters, socket]);

    useSocketEvent(Resource.DATA_EXPORT, Action.UPDATE, handleExportUpdate);
    useSocketEvent(Resource.DATA_EXPORT, Action.CREATE, handleExportCreate);

    const fetchData = useCallback(async (page = 1, filterParams = {}) => {
        if (fetchAbortControllerRef.current) {
            fetchAbortControllerRef.current.abort();
        }

        const controller = new AbortController();
        fetchAbortControllerRef.current = controller;

        try {
            setLoading(true);
            const response = await getDataExports({
                page,
                page_size: pagination.pageSize,
                filters: filterParams,
                sort_by: 'created_at',
                sort_direction: 'desc',
                signal: controller.signal,
            });
            setData(response.data);
            setPagination(prev => ({
                ...prev,
                current: response.metadata.page,
                total: response.metadata.total
            }));
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || controller.signal.aborted) return;
            handleError(error);
        } finally {
            if (fetchAbortControllerRef.current === controller) {
                setLoading(false);
            }
        }
    }, [pagination.pageSize, handleError]);

    const debouncedFetch = useMemo(
        () => debounce((page, filterParams) => {
            fetchData(page, filterParams);
        }, 500),
        [fetchData]
    );

    useEffect(() => {
        fetchData(pagination.current, normalizedFilters);
        return () => {
            if (fetchAbortControllerRef.current) {
                fetchAbortControllerRef.current.abort();
            }
            debouncedFetch.cancel?.();
        };
    }, [fetchData, debouncedFetch]);

    const handleFilterChange = (filtersArray, normalized) => {
        setFilters(filtersArray);
        setNormalizedFilters(normalized);
        setPagination(prev => ({ ...prev, current: 1 }));
        debouncedFetch(1, normalized);
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            const response = await deleteDataExport(id);
            const text = resolveBackendMessage(t, response);
            if (text) message.success(text);
            fetchData(pagination.current, normalizedFilters);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: t('created_at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => new Date(text).toLocaleString(),
            width: 180,
        },
        {
            title: t('resources'),
            dataIndex: 'resources',
            key: 'resources',
            render: (resources) => resources?.map(r => <Tag key={r}>{t(RESOURCE_LABEL_MAP[r] || r)}</Tag>),
        },
        {
            title: t('format'),
            dataIndex: 'format',
            key: 'format',
            render: (text) => <Tag color="blue">{text?.toUpperCase()}</Tag>,
            width: 100,
        },

        {
            title: t('progress'),
            dataIndex: 'progress',
            key: 'progress',
            width: 120,
            render: (progress, record) => {
                const statusObj = record.status;
                let status = 'unknown';
                if (statusObj && typeof statusObj === 'object' && statusObj.message_code) {
                    const parts = statusObj.message_code.split('.');
                    status = parts[parts.length - 1];
                } else if (typeof statusObj === 'string') {
                    status = statusObj;
                }

                const isProcessing = status === 'processing' || status === 'fetching_resource';
                const isCompleted = status === 'completed';
                const isFailed = status === 'failed';

                if (isCompleted) {
                    return <Text style={{ color: '#52c41a', fontWeight: 600 }}>100%</Text>;
                }
                if (isFailed) {
                    return <Text style={{ color: '#ff4d4f', fontWeight: 600 }}>{Math.round(progress || 0)}%</Text>;
                }
                if (isProcessing && progress != null) {
                    return <Text style={{ color: '#1677ff', fontWeight: 600 }}>{Math.round(progress)}%</Text>;
                }
                return <Text type="secondary">—</Text>;
            }
        },
        {
            title: t('actions'),
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (_, record) => {
                const statusObj = record.status;
                let isCompleted = false;
                if (statusObj && typeof statusObj === 'object' && statusObj.message_code) {
                    isCompleted = statusObj.message_code.endsWith('completed');
                } else {
                    isCompleted = statusObj === 'completed';
                }

                return (
                    <Space>
                        {isCompleted && record.download_url && (
                            <Tooltip title={t('download')}>
                                <Button
                                    type="text"
                                    icon={<DownloadOutlined />}
                                    href={record.download_url}
                                    target="_blank"
                                />
                            </Tooltip>
                        )}
                        <Popconfirm
                            title={t('delete_export_confirm')}
                            onConfirm={() => handleDelete(record.id)}
                            okText={t('yes')}
                            cancelText={t('no')}
                        >
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                );
            },
        }
    ];

    const expandedRowRender = (record) => {
        if (!record.summary?.resources) return null;

        const resources = Object.entries(record.summary.resources);
        const totalSummary = record.summary.total;

        // Resolve status for the header tag
        const statusObj = record.status;
        let status = 'unknown';
        if (statusObj && typeof statusObj === 'object') {
            if (statusObj.message_code) {
                const parts = statusObj.message_code.split('.');
                status = parts[parts.length - 1];
            } else if (statusObj.message) {
                status = statusObj.message;
            }
        } else if (typeof statusObj === 'string') {
            status = statusObj;
        }
        const isProcessing = status === 'processing' || status === 'fetching_resource';
        const isCompleted = status === 'completed';
        const isFailed = status === 'failed';
        let tagColor = 'default';
        if (isCompleted) tagColor = 'success';
        else if (isFailed) tagColor = 'error';
        else if (isProcessing) tagColor = 'processing';

        return (
            <div style={{ padding: '12px 48px 4px' }}>
                {/* Status */}
                <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
                    <Text style={{ fontSize: 12, color: '#8c8c8c' }}>{t('status')}:</Text>
                    <Tag color={tagColor} style={{ margin: 0 }}>
                        {statusObj && typeof statusObj === 'object'
                            ? resolveBackendMessage(t, statusObj)
                            : status}
                    </Tag>
                </Flex>

                {/* Summary total header */}
                {totalSummary && (
                    <Flex
                        justify="space-between"
                        align="center"
                        style={{ marginBottom: 8 }}
                    >
                        <Text strong style={{ fontSize: 13 }}>{t('total_progress', 'Total Progress')}</Text>
                        <Space size="large">
                            <span style={{ fontSize: 12, color: '#52c41a' }}>
                                {t('exported')}: <Text strong style={{ color: '#52c41a' }}>{totalSummary.exported?.toLocaleString()}</Text>
                            </span>
                            <span style={{ fontSize: 12, color: totalSummary.errors > 0 ? '#ff4d4f' : '#bfbfbf' }}>
                                {t('errors', 'Errors')}: <Text strong style={{ color: totalSummary.errors > 0 ? '#ff4d4f' : '#bfbfbf' }}>{totalSummary.errors?.toLocaleString()}</Text>
                            </span>
                            <span style={{ fontSize: 12, color: '#595959' }}>
                                {t('total')}: <Text strong>{totalSummary.total_records?.toLocaleString()}</Text>
                            </span>
                        </Space>
                    </Flex>
                )}

                {/* Global progress bar */}
                {record.progress != null && (
                    <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
                        <Progress
                            percent={Math.round(record.progress || 0)}
                            strokeColor={Math.round(record.progress || 0) >= 100 ? '#52c41a' : '#1677ff'}
                            size={[null, 8]}
                        />
                    </div>
                )}

                {/* Per-resource rows */}
                {resources.map(([resourceName, resourceData], index) => {
                    const percent = resourceData.total_records > 0
                        ? Math.round((resourceData.exported / resourceData.total_records) * 100)
                        : 0;
                    const hasErrors = resourceData.errors > 0;
                    const isResourceCompleted = resourceData.completed;
                    const color = hasErrors ? '#ff4d4f' : isResourceCompleted ? '#52c41a' : '#1677ff';
                    const resourceLabel = t(RESOURCE_LABEL_MAP[resourceName] || resourceName, resourceName.replace(/_/g, ' '));

                    return (
                        <div
                            key={resourceName}
                            style={{
                                paddingBottom: 12,
                                marginBottom: 12,
                                borderBottom: index < resources.length - 1 ? '1px dashed #f0f0f0' : 'none',
                            }}
                        >
                            <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
                                <Flex align="center" gap={8}>
                                    <div style={{ width: 3, height: 14, borderRadius: 2, background: color }} />
                                    <Text strong style={{ fontSize: 13, textTransform: 'capitalize' }}>
                                        {resourceLabel}
                                    </Text>
                                    <Tag
                                        color={hasErrors ? 'error' : isResourceCompleted ? 'success' : 'processing'}
                                        style={{ margin: 0, borderRadius: 4, fontSize: 11 }}
                                    >
                                        {hasErrors ? t('errors', 'Errors') : isResourceCompleted ? t('completed') : t('processing')}
                                    </Tag>
                                </Flex>
                                <Space size="middle" style={{ fontSize: 12 }}>
                                    <span style={{ color: '#52c41a' }}>
                                        {t('exported')}: <Text strong style={{ color: '#52c41a' }}>{resourceData.exported?.toLocaleString()}</Text>
                                    </span>
                                    <span style={{ color: hasErrors ? '#ff4d4f' : '#bfbfbf' }}>
                                        {t('errors', 'Errors')}: <Text strong style={{ color: hasErrors ? '#ff4d4f' : '#bfbfbf' }}>{resourceData.errors?.toLocaleString()}</Text>
                                    </span>
                                    <span style={{ color: '#595959' }}>
                                        {t('total')}: <Text strong>{resourceData.total_records?.toLocaleString()}</Text>
                                    </span>
                                </Space>
                            </Flex>
                            <Progress
                                percent={percent}
                                size={[null, 6]}
                                strokeColor={color}
                                trailColor="#f0f0f0"
                                showInfo={false}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };


    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({ ...prev, current: page, pageSize }));
        fetchData(page, normalizedFilters);
    };

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <DataExportForm open={isModalOpen} onClose={() => setIsModalOpen(false)} />
            <PageToolbar
                title={t('exports')}
                actions={
                    <Button
                        icon={<ExportOutlined />}
                        onClick={() => setIsModalOpen(true)}
                        type="link"
                    >
                        {!screens.xs && t('create_export')}
                    </Button>
                }
                filters={
                    <SharedFilterBar
                        fields={filterFields}
                        value={filters}
                        onChange={handleFilterChange}
                    />
                }
            />
            <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                <AppTable
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    expandable={{
                        expandedRowRender,
                        rowExpandable: (record) => !!record.summary?.resources,
                    }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        onChange: handlePageChange,
                    }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </Space>
    );
};

export default DataExportList;
